// frontend/src/utils/rosterWorkbook.js
// Turns an uploaded .xlsx into the plain 2D matrix rosterParser expects.
//
// Kept separate from the parser so the parsing rules stay testable without fixture
// files, and so ExcelJS — which is large — can be loaded only when someone actually
// uploads a workbook.

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Team lists run to a few dozen rows. A sheet far larger than this is a stray export or
// a corrupt file, and reading every cell of it would lock up the tab.
const MAX_ROWS = 5000;
const MAX_COLS = 60;

/**
 * Worksheet → 2D array of raw cell values.
 *
 * A merged cell reports the master's value from every position it covers, so the banner
 * and footer rows in these templates arrive looking like data repeated across every
 * column. Only the master keeps its value; the cells it spans are blanked.
 *
 * Takes anything shaped like an ExcelJS worksheet, so it can be exercised with a stub.
 */
export const worksheetToMatrix = (worksheet) => {
    const rowCount = Math.min(worksheet.rowCount || 0, MAX_ROWS);
    const colCount = Math.min(worksheet.columnCount || 0, MAX_COLS);
    const matrix = [];

    for (let r = 1; r <= rowCount; r++) {
        const row = worksheet.getRow(r);
        const cells = [];
        for (let c = 1; c <= colCount; c++) {
            const cell = row.getCell(c);
            const isSpannedCell = cell.isMerged && cell.master && cell.master.address !== cell.address;
            cells.push(isSpannedCell ? null : cell.value);
        }
        matrix.push(cells);
    }

    return matrix;
};

/** First worksheet with any content, falling back to the first sheet. */
export const pickWorksheet = (workbook) => {
    const sheets = workbook.worksheets || [];
    return sheets.find((ws) => (ws.rowCount || 0) > 0) || sheets[0] || null;
};

/**
 * CSV text → 2D array.
 *
 * A single pass over the whole string rather than a split on newlines, so a quoted field
 * containing a line break stays one field. The leading BOM matters: Excel's "CSV UTF-8"
 * save adds one, and left in place it corrupts the first header cell — "team_name"
 * arrives as "﻿team_name", fails the header match, and the header row gets imported
 * as if it were a player.
 */
export const csvToMatrix = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;

    const endField = () => { row.push(field); field = ''; };
    const endRow = () => { endField(); rows.push(row); row = []; };

    const clean = String(text).replace(/^﻿/, '');

    for (let i = 0; i < clean.length; i++) {
        const ch = clean[i];

        if (quoted) {
            if (ch !== '"') { field += ch; continue; }
            if (clean[i + 1] === '"') { field += '"'; i++; continue; }
            quoted = false;
            continue;
        }

        if (ch === '"') quoted = true;
        else if (ch === ',') endField();
        else if (ch === '\r') { /* handled by the \n that follows */ }
        else if (ch === '\n') endRow();
        else field += ch;
    }

    if (field !== '' || row.length) endRow();

    return rows.slice(0, MAX_ROWS).map((r) => r.slice(0, MAX_COLS));
};

/**
 * Read an uploaded roster workbook.
 *
 * Resolves to { matrix, sheetName, truncated }. Throws a message fit to show the user —
 * these files come from team managers, so "not a valid zip" needs to read as something
 * they can act on.
 */
export const readRosterWorkbook = async (file) => {
    if (!file) throw new Error('No file selected.');

    const name = file.name || '';
    if (!/\.(xlsx?|csv|txt)$/i.test(name)) {
        throw new Error('Upload the team list as .xlsx or .csv.');
    }
    if (file.size > MAX_FILE_BYTES) {
        throw new Error(`That file is ${Math.round(file.size / 1024 / 1024)} MB. Roster lists should be well under ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
    }

    // CSV needs no spreadsheet library, so skip the chunk download entirely.
    if (/\.(csv|txt)$/i.test(name)) {
        return { matrix: csvToMatrix(await file.text()), sheetName: null, truncated: false };
    }

    // The prebuilt browser bundle: importing the package root pulls ExcelJS's Node build,
    // which expects core modules webpack 5 no longer polyfills. Dynamic so it lands in
    // its own chunk rather than the main bundle.
    const { default: ExcelJS } = await import('exceljs/dist/exceljs.min.js');

    const workbook = new ExcelJS.Workbook();
    try {
        await workbook.xlsx.load(await file.arrayBuffer());
    } catch {
        throw new Error('That file could not be opened as an Excel workbook. If it was exported from another system, try opening it in Excel and saving it as .xlsx.');
    }

    const worksheet = pickWorksheet(workbook);
    if (!worksheet) throw new Error('That workbook has no sheets in it.');

    return {
        matrix: worksheetToMatrix(worksheet),
        sheetName: worksheet.name,
        truncated: (worksheet.rowCount || 0) > MAX_ROWS,
    };
};
